import { EMAIL_TEMPLATE_CREATE_METHOD_OPTIONS } from "@constants/email-template-create-method.options";
import { resources } from "@constants/hardcoded-strings";
import { ButtonWithSpinner } from "@containers/common/button-with-spinner/button-with-spinner";
import { InputFormGroup } from "@containers/common/input-form-group/input-form-group";
import { SelectFormGroup } from "@containers/common/select-form-group/select-form-group";
import { EmailTemplateSelectFormGroup } from "@containers/common/select-form-groups";
import { TemplateAvailabilityLevelSection } from "@containers/modals/add-email-template-modal/template-availability-level-section/template-availability-level-section";
import { ButtonTypes } from "@enums/button-types";
import { EmailTemplateCreateMethodTypes } from "@enums/email-template-create-method-types";
import { LabelWidthTypes } from "@enums/label-width-types";
import { SelectOption } from "@idw-types/common";
import { EmailTemplateGeneralInfoValidationErrors } from "@idw-types/common/validation/email-template/email-template-validation-errors";
import { FormattedEmailTemplate, SelfServiceVerificationWhitelabeledEmails } from "@idw-types/data/email-template";
import { IDWSession } from "@idw-types/data/idw-session";
import { AxnCompany } from "@idw-types/data/reference-data/axn";
import { ChangeEvent, FormEvent, useMemo, useState } from "react";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import Modal from "react-bootstrap/Modal";

type EmailTemplateMetadataFormProps = {
    session: IDWSession;
    labelWidth: LabelWidthTypes;
    axnCompanies: Array<AxnCompany>;
    selfServiceVerificationWhiteLabeledEmails: Array<SelfServiceVerificationWhitelabeledEmails>;
    validationErrors: EmailTemplateGeneralInfoValidationErrors;
    state: FormattedEmailTemplate;
    requestInProgress: boolean;
    emailTemplates: Array<FormattedEmailTemplate>;
    serviceOrganizationId: string;
    onModalClose: () => void;
    handleSubmit: (event: FormEvent) => void;
    setState: (state: FormattedEmailTemplate) => void;
};

export const EmailTemplateMetadataForm = (props: EmailTemplateMetadataFormProps) => {
    const {
        labelWidth,
        axnCompanies,
        selfServiceVerificationWhiteLabeledEmails,
        validationErrors,
        state,
        requestInProgress,
        session,
        serviceOrganizationId,
        emailTemplates,
        onModalClose,
        handleSubmit,
        setState
    } = props;

    type AddEmailTemplateState = {
        requestInProgress: boolean;
        selectedCreateMethod: EmailTemplateCreateMethodTypes;
        selectedEmailTemplateOption: SelectOption;
        selectedFileName: string;
        selectedFile: File;
        selectedVerificationServiceOption: SelectOption;
    };
    const [newState, setNewState] = useState<AddEmailTemplateState>({
        requestInProgress: false,
        selectedCreateMethod: EmailTemplateCreateMethodTypes.CREATE_FROM_SCRATCH || null,
        selectedEmailTemplateOption: null,
        selectedFileName: "",
        selectedFile: null,
        selectedVerificationServiceOption: null,
    });
    const handleUpdateCreateMethod = (newValue: SelectOption<EmailTemplateCreateMethodTypes>) => {
        setNewState(newState => ({
            ...newState,
            selectedCreateMethod: newValue.value
        }));
    };

    const handleUpdateName = (event: ChangeEvent<HTMLInputElement>) => {
        setState({
            ...state,
            name: event.target.value
        });
    };

    const handleUpdateEmailTemplate = (newValue: SelectOption) => {
        if (newValue) {
            setNewState(newState => ({
                ...newState,
                selectedEmailTemplateOption: newValue
            }));
            const selectedTemplate = emailTemplates.find(template => template.id === newValue.value);
            if (selectedTemplate) {
                setState({
                    ...state,
                    template: emailTemplates.find(template => template.id === newValue.value).template,
                })
            }
        }
    };
    const selectedCreateMethod: SelectOption<EmailTemplateCreateMethodTypes> = useMemo(() => {
        return EMAIL_TEMPLATE_CREATE_METHOD_OPTIONS.find(option => option.value === newState.selectedCreateMethod);
    }, [EMAIL_TEMPLATE_CREATE_METHOD_OPTIONS, newState.selectedCreateMethod]);

    const resource = resources.modals.addEmailTemplate;

    return (
        <Form onSubmit={handleSubmit}>
            <Modal.Header id={"add-email-template-title"} closeButton>
                <Modal.Title>{resource.title}</Modal.Title>
            </Modal.Header>
            <Modal.Body>

                <SelectFormGroup
                    controlId={`email-template-create-method-select`}
                    label={resource.inputs.iWantTo.label}
                    ariaLabel={resource.inputs.iWantTo.label}
                    automationId={`email-template-create-method-select`}
                    onChange={handleUpdateCreateMethod}
                    options={EMAIL_TEMPLATE_CREATE_METHOD_OPTIONS}
                    selectedOption={selectedCreateMethod}
                    disabled={requestInProgress}
                    isRequired={true}
                    isSearchable={false}
                    isClearable={false}
                    labelWidth={labelWidth}
                />
                <hr />
                {selectedCreateMethod?.value === EmailTemplateCreateMethodTypes.USE_TEMPLATE && (
                    <EmailTemplateSelectFormGroup
                        idPrefix={"email-template-select"}
                        onChange={handleUpdateEmailTemplate}
                        emailTemplates={emailTemplates}
                        selectedOption={newState.selectedEmailTemplateOption}
                        disabled={requestInProgress}
                        isRequired={true}
                        isSearchable={true}
                        isClearable={false}
                        labelWidth={labelWidth}
                    />
                )}
                <TemplateAvailabilityLevelSection
                    session={session}
                    isDisabled={requestInProgress}
                    labelWidth={labelWidth}
                    axnCompanies={axnCompanies}
                    state={state}
                    serviceOrganizationId={serviceOrganizationId}
                    setState={setState}
                    validationErrors={validationErrors}
                    selfServiceVerificationWhiteLabeledEmails={selfServiceVerificationWhiteLabeledEmails}
                />

                <hr />
                <InputFormGroup
                    controlId="email-template-name-input"
                    automationId="email-template-name-input"
                    label={resource.inputs.name.label}
                    ariaLabel={resource.inputs.name.label}
                    placeholder={resource.inputs.name.placeholder}
                    value={state.name}
                    onChange={handleUpdateName}
                    disabled={requestInProgress}
                    isRequired={true}
                    labelWidth={labelWidth}
                />
                {validationErrors.nameError}

                <hr />

            </Modal.Body>
            <Modal.Footer>
                <Button id="add-email-template-cancel-button" variant="secondary" onClick={onModalClose}>{resources.common.buttons.cancel.label}</Button>
                <ButtonWithSpinner
                    id="add-email-template-next-to-content-button"
                    disabled={requestInProgress || validationErrors.count > 0}
                    hideSpinner={validationErrors.count > 0}
                    variant="primary"
                    type={ButtonTypes.SUBMIT}
                    label={resource.buttons.nextToContent.label}
                />
            </Modal.Footer>
        </Form>
    );
};